import classNames from 'classnames';
import { useCallback } from 'react';
import { BaseAttribute, ComponentAttribute } from '../../types/attributes';
import { ATTR_NAMES } from '../../types/attrNames';
import { CmsProduct } from '../../types/cmsProduct';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import {
  sendProductAnalytics,
  UserActionEnum,
  ComponentTypeEnum,
} from '../../utils/analytics';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { formatAttributeItemValue } from '../../utils/helpers';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import Button from '../Button';
import Tooltip from '../Tooltip';
import { isPrefixCurrency } from './constants';
import { TRANSLATIONS } from './i18n';

const excludeAttributeNames: string[] = [
  ATTR_NAMES.STEPS_TO_APPLY,
  ATTR_NAMES.PROMO_CODE,
  ATTR_NAMES.POLICY_WORDING_DOCUMENT_LINK,
];

type SectionAttributesProps = {
  product: CmsProduct;
  componentAttributes: Array<ComponentAttribute>;
  calculateError?: boolean;
  index: string;
  highlightFirstAttribute?: boolean;
  url?: string;
  pushDataLayer: PushDataLayerFn;
};

type AttributeType = {
  label: string;
  additionalInfo: string;
  attribute: BaseAttribute;
};

export const SectionAttributes: React.FC<SectionAttributesProps> = (props) => {
  const {
    componentAttributes,
    product,
    calculateError,
    highlightFirstAttribute,
    url,
    index,
    pushDataLayer,
  } = props;
  const { attributes: productAttributes } = product;
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const pushTooltipAnalytic = useCallback(
    (type: 'click' | 'hover', label: string) => {
      sendProductAnalytics({
        action: type === 'click' ? UserActionEnum.CLICK : UserActionEnum.HOVER,
        componentTitle: `Product Card/Tooltip_${label}`,
        componentType: ComponentTypeEnum.TOOLTIP,
        event: 'product_interaction',
        index,
        url,
        product,
        pushDataLayer,
      });
    },
    [url, index, product, pushDataLayer],
  );

  const attributes: Array<AttributeType> = componentAttributes
    .map((ca) => {
      const productAttr = productAttributes?.find(
        (pa) => pa?.name?.uid === ca.attribute?.uid,
      );
      let additionalInfo = ca.additional_information;
      if (
        productAttr?.__typename === 'ComponentAttributesAttrNumber' &&
        productAttr?.additional_information
      ) {
        additionalInfo = productAttr?.additional_information;
      }

      return {
        label: ca.label ? ca.label : ca.attribute?.label,
        additionalInfo: additionalInfo,
        attribute: productAttr,
      };
    })
    .filter((a): a is AttributeType => {
      return (
        !!a.attribute &&
        !excludeAttributeNames.includes(a.attribute.name?.name ?? '')
      );
    });

  const renderValue = (attr: AttributeType, highlight: boolean) => {
    const attributeValue = formatAttributeItemValue(
      attr.attribute,
      true,
      true,
      false,
    );

    const type = attr.attribute.__typename;

    const prefix =
      (type === 'ComponentAttributesAttrNumber' ||
        type === 'ComponentAttributesAttrNumberRange') &&
      attributeValue.trim() !== '-'
        ? attr.attribute.prefix
        : undefined;

    const showPrefixAsOverlay = isPrefixCurrency(prefix);

    return (
      <span
        className={classNames('relative alpha-bullet-2', {
          'bg-fill/primary-light rounded-radius/tooltip px-spacing/gap/soft-8px py-spacing/gap/soft-4px':
            highlight,
        })}
      >
        {prefix && !showPrefixAsOverlay && <span>{prefix} </span>}

        {showPrefixAsOverlay && (
          <>
            <span className="invisible text-xs">{prefix} </span>
            <div
              className={classNames(
                'absolute -top-0 text-text/secondary-lighter text-xs',
                {
                  'left-2 top-1': highlight,
                },
              )}
            >
              {prefix}
            </div>
          </>
        )}

        {attributeValue}
      </span>
    );
  };

  return (
    <div className="my-6 grid grid-cols-2 gap-y-4 md:grid-cols-4">
      {attributes.map((attr, index) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={`${attr.attribute?.name?.uid}_${index}`}
          className="flex flex-col gap-2"
        >
          <div className="mhg-alpha-caption text-text/secondary-lighter">
            {attr.label}
          </div>
          <span className="mhg-alpha-bullet-2 lg:mhg-alpha-bullet-1 flex h-full items-center gap-1">
            {calculateError
              ? tr('calculateErrorPlaceholderMessage', staticText)
              : attr.attribute
                ? renderValue(
                    attr,
                    (index === 0 && highlightFirstAttribute) ?? false,
                  )
                : ' - '}
            {attr.additionalInfo && (
              <span className="inline">
                <Button
                  variant="text"
                  className="p-1"
                  onClick={() => pushTooltipAnalytic('click', attr.label ?? '')}
                  onMouseEnter={() =>
                    pushTooltipAnalytic('hover', attr.label ?? '')
                  }
                >
                  <Tooltip
                    content={attr.additionalInfo}
                    popup-style="max-w-[300px]"
                  />
                </Button>
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};
